/**
 * API-1810 — admin-issued account-recovery magic link (hello@ recovery).
 *
 * A locked-out user who cannot receive mail at their account address writes
 * to hello@. After the identity check in
 * `docs/operator-runbooks/account-recovery.md`, an admin mints a single-use
 * magic link to a NEW address through
 * `POST /api/v1/admin/account-recovery/links`. Consuming it moves the account
 * to that address and marks it verified. No password is collected or set.
 */
import { z } from 'zod';
import { emailSchema } from '../auth';

/**
 * Accepted proof classes. `linked_provider_proof`: the requester proved
 * control of an OAuth provider already linked to the account.
 * `other_documented`: other evidence the runbook accepts, described in the note.
 */
export const ACCOUNT_RECOVERY_EVIDENCE_TYPES = [
  'linked_provider_proof',
  'other_documented',
] as const;
export type AccountRecoveryEvidenceType = (typeof ACCOUNT_RECOVERY_EVIDENCE_TYPES)[number];

/** Long enough to force a real description of the evidence, short enough for an audit line. */
export const ACCOUNT_RECOVERY_EVIDENCE_NOTE_MIN = 10;
export const ACCOUNT_RECOVERY_EVIDENCE_NOTE_MAX = 1000;

/** Body for `POST /api/v1/admin/account-recovery/links`. */
export const adminAccountRecoveryLinkSchema = z
  .object({
    userId: z.string().trim().min(1).max(128),
    newEmail: emailSchema,
    evidenceType: z.enum(ACCOUNT_RECOVERY_EVIDENCE_TYPES),
    evidenceNote: z
      .string()
      .trim()
      .min(ACCOUNT_RECOVERY_EVIDENCE_NOTE_MIN)
      .max(ACCOUNT_RECOVERY_EVIDENCE_NOTE_MAX),
  })
  .strict();
export type AdminAccountRecoveryLinkBody = z.infer<typeof adminAccountRecoveryLinkSchema>;

/**
 * Refusal reasons, returned as the error `code`; the audit `reason` is the
 * lowercase form. `NOT_FOUND` covers missing and soft-deleted users.
 * `AGENT_ACCOUNT`: `isE2EAccount` or an agent-session principal.
 * `SELF_RECOVERY`: an admin cannot recover their own account.
 * `SUPER_ADMIN_TARGET`: only a SUPER_ADMIN may recover a SUPER_ADMIN.
 * `PLACEHOLDER_EMAIL`: a synthesized `@users.loci.invalid` OAuth placeholder is not a mailbox.
 * `SAME_EMAIL`: the new address is the account's current one.
 * `EMAIL_TAKEN`: another account holds or has reserved the new address.
 */
export const ACCOUNT_RECOVERY_REFUSAL_CODES = [
  'NOT_FOUND',
  'AGENT_ACCOUNT',
  'SELF_RECOVERY',
  'SUPER_ADMIN_TARGET',
  'PLACEHOLDER_EMAIL',
  'SAME_EMAIL',
  'EMAIL_TAKEN',
] as const;
export type AccountRecoveryRefusalCode = (typeof ACCOUNT_RECOVERY_REFUSAL_CODES)[number];

/** `201` response. Never carries the token or the full new address. */
export const adminAccountRecoveryLinkResponseSchema = z
  .object({
    recoveryLinkId: z.string().min(1),
    userId: z.string().min(1),
    /** `j•••@example.com` — enough for the operator to confirm the target. */
    maskedNewEmail: z.string().min(1),
    expiresAt: z.string().datetime(),
  })
  .strict();
export type AdminAccountRecoveryLinkResponse = z.infer<
  typeof adminAccountRecoveryLinkResponseSchema
>;
